/**
 * ContentDetail.tsx — FR-015 / T316.
 *
 * The wired surface for the on-demand audio rendition retry orphan
 * (specs/exploration/stub_orphan_inventory.md §5 orphan #5):
 *
 *   - api.audio.requestAudioRetry  (mutation, requirePermission
 *     `audio_retry` → admin/editor per PRD §A.6)
 *
 * The retry mutation transitions a `failed` audio rendition back to
 * `pending` and re-enqueues the publish path. Routed through
 * useMutationWithToast (FR-013 (H1) / FR-015 (O-D)) — argument KEYS
 * only flow to the log sink.
 *
 * The detail page itself is re-exported from `_bundle.tsx`; this file
 * appends an honest-state "reintentar audio" affordance for the
 * audio_retry permission.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { api } from '@convex/api';
import { AccessGate, Btn, Panel } from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { ContentDetail as ContentDetailShell } from './_bundle';

interface ContentDetailProps {
  id?: string;
}

export function ContentDetail(props: ContentDetailProps) {
  const retryM = useMutationWithToast(api.audio.requestAudioRetry, {
    logLabel: 'audio_requestRetry',
    successMessage: 'Reintento de audio encolado.',
    errorMessagePrefix: 'No se pudo reintentar el audio',
  });

  const handleRetry = () => {
    if (!props.id) return;
    void retryM.run({ assetId: props.id as any });
  };

  return (
    <div>
      <ContentDetailShell {...props} />
      <Panel
        title="Reintento de audio (manual)"
        style={{ marginTop: 24 }}
      >
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 14,
            color: 'var(--body-muted)',
          }}
        >
          Si la generación de audio falló, vuelve a intentarla manualmente.
          Solo administradores y editores pueden ejecutar este reintento; el
          servicio transiciona <code>audioRendition.state</code> de{' '}
          <code>failed</code> a <code>pending</code>.
        </p>
        <AccessGate perm="audio_retry">
          <Btn
            variant="solid"
            icon="refresh"
            disabled={retryM.loading || !props.id}
            onClick={handleRetry}
          >
            Reintentar audio
          </Btn>
        </AccessGate>
      </Panel>
    </div>
  );
}

export default ContentDetail;
